/**
 * @fileoverview Presence indicator showing which users are in the current budget.
 *
 * Displays avatars/initials of other users currently viewing or editing
 * the same budget. Helps users be aware of potential conflicts.
 */

import React from 'react';
import { useRealtime } from '../../contexts/RealtimeContext';
import styles from './PresenceIndicator.module.css';

/**
 * PresenceIndicator component.
 *
 * @param {Object} props - Component props
 * @param {number} props.currentUserId - The current user's ID (to exclude from display)
 */
export function PresenceIndicator({ currentUserId }) {
    const { usersInBudget, isConnected } = useRealtime();

    // Filter out the current user
    const otherUsers = usersInBudget.filter(u => u.userId !== currentUserId);

    // Don't render if no other users or not connected
    if (!isConnected || otherUsers.length === 0) {
        return null;
    }

    // Get initials from name
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length === 1) {
            return parts[0].charAt(0).toUpperCase();
        }
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    // Generate a consistent color from the user ID
    const getColor = (userId) => {
        const colors = [
            '#3b82f6', // blue
            '#10b981', // green
            '#f59e0b', // amber
            '#ef4444', // red
            '#8b5cf6', // purple
            '#ec4899', // pink
            '#06b6d4', // cyan
            '#f97316', // orange
        ];
        return colors[userId % colors.length];
    };

    return (
        <div className={styles.container}>
            <span className={styles.label}>Also here:</span>
            <div className={styles.avatars}>
                {otherUsers.slice(0, 5).map(user => (
                    <div
                        key={user.userId}
                        className={`${styles.avatar} ${user.editing ? styles.editing : ''}`}
                        style={{ backgroundColor: getColor(user.userId) }}
                        title={`${user.userName}${user.editing ? ` (editing ${user.editing.type})` : ''}`}
                    >
                        {getInitials(user.userName)}
                        {user.editing && (
                            <span className={styles.editingDot} />
                        )}
                    </div>
                ))}
                {otherUsers.length > 5 && (
                    <div className={styles.more}>
                        +{otherUsers.length - 5}
                    </div>
                )}
            </div>
        </div>
    );
}

export default PresenceIndicator;
